<template>
  <div class="edit-amount-popup ">
    <popup :title="`כמה כסף צפוי לצאת על ${expenseCategory} ב${timeFrameTitle}?`"
           :buttons="[{ title: 'עדכון הסכום הצפוי', action: updateEnvelopeAmountApprove }]"
           :close-action="cancelEdit">
      <template v-slot:content>
        <div class="content-container">
          <edit-amount-input
            :updated-amount="updatedAmount"
            :placeholder="placeholder"
            :on-input="onInput"/>
          <div class="error-container">
            <div v-if="displayErrorMessage" class="error-message">{{ errorMessage }}</div>
          </div>
        </div>
      </template>
    </popup>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import BaseUI from 'base-ui';
import { mapGetters, mapActions } from 'vuex';
import constants from '../../../../../../../../../constants/cashflow-view';
import Segment from '../../../../../../../../../Segment';
import money from '../../../../../../../../../utils/money';

export default {
  name: 'EditEconomyAmountPopup',
  components: {
    Popup: BaseUI.Popup,
    EditAmountInput: BaseUI.EditAmountInput,
  },
  data() {
    return {
      updatedAmount: null,
      displayErrorMessage: false,
    };
  },
  props: {
    expenseCategory: {
      type: String,
      required: true,
    },
    category: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('cashflowView', ['monthName']),
    timeFrame() {
      return this.category.timeFrameDetails.timeFrame;
    },
    startDate() {
      return this.category.timeFrameDetails.startDate;
    },
    endDate() {
      return this.category.timeFrameDetails.endDate;
    },
    weeklyIndex() {
      return this.category.timeFrameDetails.weeklyIndex;
    },
    originalAmount() {
      return this.category.recommendedAmount;
    },
    placeholder() {
      return money.formatAmountWithFraction(this.originalAmount, 1);
    },
    envelopeId() {
      return this.category.envelopeId;
    },
    actualsSum() {
      return _.round(this.category.actualsSum, 1);
    },
    timeFrameTitle() {
      if (this.timeFrame === constants.envelopeTimeFrames.weekly) {
        const startDate = moment(this.startDate).format('D.M');
        const endDate = moment(this.endDate).format('D.M');
        return `שבוע ${this.weeklyIndex + 1} (מ-${startDate} עד ${endDate})`;
      }
      return this.monthName;
    },
    errorMessage() {
      if (this.updatedAmount < this.actualsSum) {
        return this.ERROR_MESSAGES.LESS_THAN_ACTUAL_SUM;
      } if (!this.updatedAmount || this.updatedAmount === 0) {
        return this.ERROR_MESSAGES.AMOUNT_IS_ZERO;
      }

      return null;
    },
    timeFrameDescription() {
      return this.timeFrame === constants.envelopeTimeFrames.weekly ? 'שבוע' : 'חודש';
    },
    ERROR_MESSAGES() {
      return {
        LESS_THAN_ACTUAL_SUM: `הסכום הצפוי צריך להיות מעל ${this.actualsSum} ₪ שכבר יצאו על ${this.expenseCategory} ה${this.timeFrameDescription}`,
        AMOUNT_IS_ZERO: 'הסכום הצפוי צריך להיות מעל 0 ₪',
      };
    },
  },
  methods: {
    ...mapActions('editCashflow', ['updateEnvelopeAmount']),
    updateEnvelopeAmountApprove() {
      if (this.errorMessage) {
        Segment.trackUserInteraction('EditAmountErrorShown', {
          expenseCategory: this.expenseCategory,
          envelopeId: this.envelopeId,
          oldAmount: this.originalAmount,
          newAmount: this.updatedAmount,
        });
        this.displayErrorMessage = true;
        return;
      }
      Segment.trackUserInteraction('EditAmountApprovedClicked', {
        expenseCategory: this.expenseCategory,
        envelopeId: this.envelopeId,
        oldAmount: this.originalAmount,
        newAmount: this.updatedAmount,
      });
      this.updateEnvelopeAmount({ envelopeId: this.envelopeId, amount: this.updatedAmount });
      this.closeModal();
    },
    onInput(value) {
      this.displayErrorMessage = false;
      this.updatedAmount = value;
    },
    closeModal() {
      this.$emit('close');
    },
    cancelEdit() {
      Segment.trackUserInteraction('EditAmountCanceled', {
        expenseCategory: this.expenseCategory,
        envelopeId: this.envelopeId,
      });
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';

  .edit-amount-popup {
    .content-container {
      padding-bottom: 13px;

      .input-holder {
        .update-amount-input {
          color: $riseup_black;
          padding: 13px 8px 8px;
          width: 206px;
          height: 64px;
          font-size: 46px;
          line-height: 46px;
          font-family: SimplerPro;
          font-weight: bold;
          border-radius: 2px;
          box-shadow: inset 0 0 6px 0 $riseup-black;
          border: solid 1px $riseup_black;
          background-color: $riseup_white;
          outline: none;

          /* Remove up and down arrow */
          -moz-appearance: textfield;
          &::-webkit-inner-spin-button {
            -webkit-appearance: none;
          }
          &::placeholder {
            color: $riseup_gray_0;
          }
        }

        .nis-currency {
          vertical-align: middle;
          font-weight: bold;
          font-size: 32px;
          color: $riseup_black;
        }
      }

      .error-container {
        position: relative;
        width: 206px;

        .error-message {
          position: absolute;
          top: 6px;
          color: $riseup_coral;
          font-size: 10px;
          font-family: SimplerPro;
          font-weight: bold;
        }
      }
    }
  }

</style>
